import { DisplayData, TenantOrderLine } from '~/lib/model';
import { Big } from 'big.js';
import TwtJob from '~/tenants/twt/model/TwtJob';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import { TwtPropertyType } from '~/tenants/twt/model/TwtOrder';
import { PersonaType } from '~/tenants/common/TenantPersona';
import TwtReelConfig from '~/tenants/twt/performable/reel/TwtReelConfig';
import { DeliverableType } from '~common/model/Deliverable';
import { TwtOrderType } from '~/tenants/twt/model/enums';
import { OrderCustomTag } from '~/lib/schema/order';
import { twt_version, TwtVersion } from '~/tenants/twt/data';

export const TWT_REEL_VOICEOVER = 100;
export const TWT_REEL_RAW = 75;

export class TwtReelJob extends TwtJob<typeof TwtReelConfig> {
  get performable(): typeof TwtReelConfig {
    return TwtReelConfig;
  }

  get eligible(): boolean {
    return (
      this.order.metadata.type === TwtOrderType.FOR_SALE_LISTING || this.order.metadata.type === TwtOrderType.COMMERCIAL
    );
  }

  defaultValue(): ZodVersionedMetadata<(typeof TwtReelConfig)['schema']> {
    // TODO: type fix?
    return {
      version: schema_latest_version(TwtReelConfig.schema),
    } as any;
  }

  get short(): string {
    return 'REEL';
  }

  get deliverable(): DeliverableType {
    return DeliverableType.SOCIAL;
  }

  get tags(): OrderCustomTag[] {
    const tags = super.tags;

    if (this.metadata.voiceover) {
      tags.push({
        name: 'Cameo',
        color: 'red',
      });
    }

    return tags;
  }

  onsite(): number {
    if(this.order.metadata.transfer){
      return 0;
    }

    let onsite = this.provider?.config.reel_onsite ?? 30;

    if (this.metadata.voiceover) {
      onsite += this.provider?.config.cameo_onsite ?? 30;
    }

    return onsite;
  }

  get configurable(): boolean {
    return true;
  }

  get media(): PersonaType {
    return PersonaType.PROVIDER;
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.voiceover) {
      info.push({
        name: 'Voiceover',
        value: 'Yes',
        provider: true,
        schedule: true,
      });
    }

    return info;
  }

  expenseLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    let price = 250;

    if(twt_version(this.order.created, TwtVersion.V21) && this.sqft >= 3000){
      price = 275;
    }

    lines.push({
      amount: new Big(price),
      description: '45-60 Second Video',
      id: 'reel',
    });

    if (this.metadata.voiceover) {
      lines.push({
        amount: new Big(80),
        description: 'Agent Cameo / Voiceover',
        id: 'voiceover',
      });
    }

    if (this.metadata.raw) {
      lines.push({
        amount: new Big(50),
        description: 'Raw Social Footage',
        id: 'raw',
      });
    }

    return lines;
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    let price = 295;

   if(this.order.metadata.type === TwtOrderType.COMMERCIAL){
      price = 375;
    }else if(twt_version(this.order.created, TwtVersion.V21) && this.sqft >= 3000){
      price = 320;
    }

    lines.push({
      amount: new Big(price),
      description: this.metadata.voiceover ? '45-60 Second Video' : this.performable.name,
      discountable: true,
      id: 'reel',
      taxable: true,
    });

    if (this.metadata.voiceover) {
      lines.push({
        amount: new Big(100),
        description: 'Agent Cameo / Voiceover',
        discountable: true,
        taxable: true,
        id: 'voiceover',
      });
    }

    if (this.metadata.raw) {
      lines.push({
        amount: new Big(TWT_REEL_RAW),
        description: 'Raw Social Footage',
        discountable: true,
        taxable: true,
        id: 'raw',
      });
    }

    return lines;
  }
}
